/** MVP tearsheet appearance. Kept here for Storybook; runtime catalog lives in GET /tearsheets/skins. */
export type TearsheetThemeId = 'slate' | 'black_orange' | 'grey_orange' | 'institutional' | 'traditional';

export type TearsheetTheme = {
  bg: string;
  panel: string;
  panel2: string;
  text: string;
  muted: string;
  accent: string;
  border: string;
};

export const TEARSHEET_THEMES: Record<TearsheetThemeId, TearsheetTheme> = {
  slate: {
    bg: '#0b1220',
    panel: '#111c2e',
    panel2: '#16243a',
    text: '#f8fafc',
    muted: '#9aa8bd',
    accent: '#4fb3ff',
    border: '#25354d',
  },
  black_orange: {
    bg: '#0b0b0d',
    panel: '#151518',
    panel2: '#202024',
    text: '#f7f7f2',
    muted: '#a7a7a2',
    accent: '#d98b20',
    border: '#2d2d32',
  },
  grey_orange: {
    bg: '#1f1f1f',
    panel: '#2b2b2b',
    panel2: '#343434',
    text: '#f7f7f2',
    muted: '#c6c6c2',
    accent: '#f59e0b',
    border: '#4a4a4a',
  },
  institutional: {
    bg: '#f6f8fb',
    panel: '#ffffff',
    panel2: '#f0f4f9',
    text: '#111827',
    muted: '#64748b',
    accent: '#17324d',
    border: '#dce3ee',
  },
  traditional: {
    bg: '#ffffff',
    panel: '#ffffff',
    panel2: '#f8fafc',
    text: '#111827',
    muted: '#4b5563',
    accent: '#1f2937',
    border: '#d1d5db',
  },
};

export const TEARSHEET_THEME_OPTIONS: Array<{ value: TearsheetThemeId | 'none'; title: string }> = [
  { value: 'none', title: 'None (product theme)' },
  { value: 'slate', title: 'Slate' },
  { value: 'black_orange', title: 'Near black / orange' },
  { value: 'grey_orange', title: 'Grey / orange' },
  { value: 'institutional', title: 'Institutional' },
  { value: 'traditional', title: 'Traditional' },
];

export function isTearsheetThemeDark(themeId: TearsheetThemeId): boolean {
  return themeId === 'slate' || themeId === 'black_orange' || themeId === 'grey_orange';
}

function srgbChannel(hex: string, index: number): number {
  const raw = hex.trim().replace('#', '');
  const pair = raw.length === 3 ? `${raw[index] ?? 'f'}${raw[index] ?? 'f'}` : raw.slice(index * 2, index * 2 + 2);
  const n = Number.parseInt(pair, 16);
  const c = Number.isFinite(n) ? n / 255 : 1;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Light/dark from `bg` luminance so custom skins follow the same chart polarity as stock. */
export function isDarkTearsheetSkin(colors: Pick<TearsheetTheme, 'bg'>): boolean {
  return 0.2126 * srgbChannel(colors.bg, 0) + 0.7152 * srgbChannel(colors.bg, 1) + 0.0722 * srgbChannel(colors.bg, 2) < 0.5;
}

export type TearsheetSkinChip = {
  skinId: TearsheetThemeId;
  name: string;
  swatch: string;
  isLight: boolean;
};

function skinChip(skinId: TearsheetThemeId, name: string): TearsheetSkinChip {
  const theme = TEARSHEET_THEMES[skinId];
  return {
    skinId,
    name,
    swatch: `linear-gradient(135deg, ${theme.bg}, ${theme.accent})`,
    isLight: !isTearsheetThemeDark(skinId),
  };
}

/** Same five MVP skins as the backend catalog. Storybook and tests use this copy. */
export const SKINS: TearsheetSkinChip[] = [
  skinChip('institutional', 'Institutional'),
  skinChip('slate', 'Slate'),
  skinChip('black_orange', 'Near black'),
  skinChip('grey_orange', 'Grey / orange'),
  skinChip('traditional', 'Traditional'),
];

/** Overrides product `--hf-scrollbar-*` vars inside a tearsheet root (skin `border` vanishes on light panels). */
export function tearsheetScrollbarVars(skin: TearsheetTheme): Record<string, string> {
  return {
    '--hf-scrollbar-thumb': skin.muted,
    '--hf-scrollbar-thumb-hover': skin.text,
    '--hf-scrollbar-track': skin.panel2,
  };
}
